import Alpine from 'alpinejs';
import { normalize } from '../../utils/helpers.js';
import { searchInitiated, viewItemList } from '../../utils/dataLayer.js';

Alpine.store('search', {
    // Core search params
    where: '',
    whereData: null, // { lat: number, lng: number, slug?: string }
    mapAreaLabel: null, // 'Homes in map area' or 'Search vacation homes in X'
    _visualWhereMatch: null, // When set, overlay shows only when where === this value
    visualLabel: null, // Computed by Alpine.effect in init()
    executedWhere: '', // Snapshot of 'where' at the time of search execution
    dates: '',
    sort: 'relevance', // relevance | price_low | price_high | rating_high
    guests: 1,
    adults: 1,
    children: 0,
    pets: false,

    // Visibility State (Manually managed for stability)
    showResults: true,
    showSkeleton: false,

    // Filter state
    filters: {
        propertyType: [],
        priceMin: 0,
        priceMax: 10000,
        bedrooms: 1,
        beds: 1,
        bathrooms: 1,
        amenities: [],
        accessibilityFeatures: [],
    },
    // Core params

    // Map bounds state (for search-as-you-move)
    bounds: null, // { north, south, east, west }
    boundsSearchEnabled: false, // true when user has moved map
    currentAbortController: null, // For cancelling in-flight requests
    lastIssuedRequestId: 0,    // Track which request was last issued (for "latest wins")
    _lastSearchSource: 'user',  // Track source of last search ('bounds' | 'filters' | 'destination' | 'user')
    hoveredListingId: null, // ID of listing currently hovered on map
    cardHoveredListingId: null, // ID of listing currently hovered on card (for marker highlighting)

    // Live filter count state (preview inside modal, does NOT touch results.filteredCount)
    _filterCountController: null,
    _debouncedFilterCount: null,
    filterCountLoading: false,
    liveFilterCount: null,

    // Results state
    results: {
        listings: [],
        mapPoints: [],
        listingPrices: {},
        pricedNights: 0,
        totalCount: 0,
        filteredCount: 0,
    },

    // Pagination state
    pagination: {
        currentPage: 1,
        limit: 12,
        offset: 0,
        totalPages: 1,
    },

    // UI state
    ui: {
        isLoading: false,
        isLoadingMore: false,
        hasSearched: false,
        error: null,
        validationMessage: null,
        backendMessage: null, // Message from backend API (e.g., "Please select a destination")
        mapMode: false,
        mapModeUiToggled: true
    },

    // Price range defaults for reset
    priceDefaults: {
        min: 0,
        max: 10000,
    },

    formatPriceDisplay(value) {
        return '$' + Number(value).toLocaleString();
    },

    handlePriceInput(field, rawValue) {
        const num = parseInt(rawValue.replace(/[^0-9]/g, '') || '0', 10);
        if (field === 'min') {
            this.filters.priceMin = Math.max(this.priceDefaults.min, Math.min(num, this.filters.priceMax));
        } else {
            this.filters.priceMax = Math.min(this.priceDefaults.max, Math.max(num, this.filters.priceMin));
        }
    },

    // Helper to calculate visibility deterministically
    updateVisibility() {
        const loading = this.ui.isLoading;
        const hasListings = this.results.listings.length > 0;
        const hasSearched = this.ui.hasSearched;
        const error = this.ui.error;

        // Simplified Loading Logic:
        // - Show skeleton whenever loading (any search type)
        // - Show results when we have listings AND not loading
        // - Show empty/initial state when not loading and no listings

        if (loading) {
            // Keep existing results visible during loading to prevent map hide/jank.
            // Only show skeleton when there are no listings yet.
            if (hasListings) {
                this.showResults = true;
                this.showSkeleton = false;
            } else {
                this.showResults = false;
                this.showSkeleton = true;
            }
        } else if (hasListings) {
            // Show results, hide skeleton
            this.showResults = true;
            this.showSkeleton = false;
        } else {
            // No listings and not loading - show empty/initial state
            this.showResults = hasSearched;
            this.showSkeleton = false;
        }

        // Error overrides everything
        if (error) {
            this.showResults = false;
            this.showSkeleton = false;
        }
    },

    init() {
        this.parseUrlParams();
        this.updateVisibility();
        // Auto-search when a destination or bounds are present
        if (window.location.pathname === '/search') {
            if (this.where || this.boundsSearchEnabled) {
                this.performSearch({ resetPagination: false, source: this.boundsSearchEnabled ? 'bounds' : 'user' });
            }
        }

        // Compute visualLabel reactively
        Alpine.effect(() => {
            if (!this.mapAreaLabel) {
                this.visualLabel = null;
            } else if (this._visualWhereMatch) {
                this.visualLabel = this.where === this._visualWhereMatch ? this.mapAreaLabel : null;
            } else {
                this.visualLabel = !this.where ? this.mapAreaLabel : null;
            }
        });

        // Clear validation message when a destination is selected
        Alpine.effect(() => {
            if (this.whereData && this.ui.validationMessage) {
                this.ui.validationMessage = null;
            }
        });
    },

    // Parse URL params to restore search state
    parseUrlParams() {
        const params = new URLSearchParams(window.location.search);

        // Core params
        if (params.get('where')) this.where = params.get('where');
        if (params.get('lat') && params.get('lng')) {
            this.whereData = {
                lat: parseFloat(params.get('lat')),
                lng: parseFloat(params.get('lng')),
            };
        }
        if (params.get('bounds')) {
            const parts = params.get('bounds').split(',').map(Number);
            if (parts.length === 4 && parts.every(n => !isNaN(n))) {
                const [north, south, east, west] = parts;
                this.bounds = { north, south, east, west };
                this.boundsSearchEnabled = true;
                this.mapAreaLabel = 'Homes in map area';
            }
        }
        if (params.get('dates')) this.dates = params.get('dates');
        if (params.get('sort')) this.sort = params.get('sort');
        if (params.get('guests')) this.guests = parseInt(params.get('guests')) || 0;
        if (params.get('adults')) this.adults = parseInt(params.get('adults')) || 0;
        if (params.get('children')) this.children = parseInt(params.get('children')) || 0;
        if (params.get('pets')) this.pets = params.get('pets') === '1';

        // Filter params
        if (params.get('propertyType')) {
            this.filters.propertyType = params.get('propertyType').split(',').filter(Boolean);
        }
        if (params.get('minPrice')) this.filters.priceMin = parseInt(params.get('minPrice')) || this.priceDefaults.min;
        if (params.get('maxPrice')) this.filters.priceMax = parseInt(params.get('maxPrice')) || this.priceDefaults.max;
        if (params.get('bedrooms')) this.filters.bedrooms = parseInt(params.get('bedrooms')) || 1;
        if (params.get('beds')) this.filters.beds = parseInt(params.get('beds')) || 1;
        if (params.get('bathrooms')) this.filters.bathrooms = parseInt(params.get('bathrooms')) || 1;
        if (params.get('amenities')) {
            this.filters.amenities = params.get('amenities').split(',').filter(Boolean);
        }
        if (params.get('accessibilityFeatures')) {
            this.filters.accessibilityFeatures = params.get('accessibilityFeatures').split(',').filter(Boolean);
        }

        // Pagination
        if (params.get('page')) {
            this.pagination.currentPage = parseInt(params.get('page')) || 1;
            this.pagination.offset = (this.pagination.currentPage - 1) * this.pagination.limit;
        }
    },

    // Helper: Build the search URL string from current state
    buildSearchUrl() {
        const params = new URLSearchParams();

        // Core params
        if (this.where) params.set('where', this.where);
        if (this.whereData?.lat != null && this.whereData?.lng != null) {
            params.set('lat', this.whereData.lat);
            params.set('lng', this.whereData.lng);
        }
        if (this.bounds && this.mapAreaLabel) {
            params.set('bounds', `${this.bounds.north},${this.bounds.south},${this.bounds.east},${this.bounds.west}`);
        }
        if (this.dates) params.set('dates', this.dates);
        if (this.sort !== 'relevance') params.set('sort', this.sort);
        if (this.guests > 1) params.set('guests', this.guests);
        if (this.adults > 1) params.set('adults', this.adults);
        if (this.children > 0) params.set('children', this.children);
        if (this.pets) params.set('pets', '1');

        // Filter params (only if non-default)
        if (this.filters.propertyType.length) {
            params.set('propertyType', this.filters.propertyType.join(','));
        }
        if (this.filters.priceMin !== this.priceDefaults.min) {
            params.set('minPrice', this.filters.priceMin);
        }
        if (this.filters.priceMax !== this.priceDefaults.max) {
            params.set('maxPrice', this.filters.priceMax);
        }
        if (this.filters.bedrooms > 1) params.set('bedrooms', this.filters.bedrooms);
        if (this.filters.beds > 1) params.set('beds', this.filters.beds);
        if (this.filters.bathrooms > 1) params.set('bathrooms', this.filters.bathrooms);
        if (this.filters.amenities.length) {
            params.set('amenities', this.filters.amenities.join(','));
        }
        if (this.filters.accessibilityFeatures.length) {
            params.set('accessibilityFeatures', this.filters.accessibilityFeatures.join(','));
        }

        // Pagination
        if (this.pagination.currentPage > 1) {
            params.set('page', this.pagination.currentPage);
        }

        const queryString = params.toString();
        return queryString ? `/search?${queryString}` : '/search';
    },

    // Sync current state to URL history (without reload)
    syncUrlState() {
        const url = this.buildSearchUrl();
        window.history.replaceState({}, '', url);
    },

    // Submit search: Redirect or update in-place
    submitSearch(source) {

        // Validation: Require a selected destination (whereData) or at least text input
        if (!this.where && !this.whereData) {
            this.ui.validationMessage = "Please select a destination";
            setTimeout(() => { this.ui.validationMessage = null; }, 3000);
            return;
        }

        // If user typed text but hasn't selected from the dropdown, auto-select first suggestion
        if (this.where && !this.whereData) {
            const whereField = document.getElementById('hero-where-field');
            if (whereField) {
                whereField.dispatchEvent(new CustomEvent('autocomplete:select-first', { bubbles: false }));
                return;
            }
        }

        console.log('Search Store: submitSearch called', { where: this.where });

        // Cancel any in-flight request to prevent stale results
        if (this.currentAbortController) {
            this.currentAbortController.abort();
            this.currentAbortController = null;
        }

        // Cancel pending timeout (if user changed search before 750ms elapsed)
        if (window.Alpine?.store?.('searchMap')?.cancelPendingBoundsSearch) {
            window.Alpine.store('searchMap').cancelPendingBoundsSearch();
        }

        // Clear bounds when user changes search (map will auto-fit to new results)
        this.clearBounds();

        // If we are not on the search page, redirect
        if (window.location.pathname !== '/search') {
            const url = this.buildSearchUrl();
            console.log('Search Store: Redirecting to', url);
            window.location.href = url;
            return;
        }

        // Track search initiation
        searchInitiated(this);

        // If we are already on search page, update URL and fetch
        this.syncUrlState();
        this.performSearch({ resetPagination: true, source: source || 'destination' });
    },

    // Navigate to search page with current store state (no validation)
    goToSearch() {
        window.location.href = this.buildSearchUrl();
    },

    // Main search method (two-phase)
    async performSearch(options = {}) {
        const { resetPagination = true, requestId = null, source = 'user' } = options;

        if (window.__AVARI_DEBUG_MAP_SEARCH) {
            console.log('[SEARCH] performSearch called', { requestId, source });
        }

        // Track the latest request ID we've issued
        // Ensure we have a request ID for tracking race conditions
        // Use high-precision timing + random to ensure uniqueness even in rapid-fire scenarios
        const currentRequestId = requestId || `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        this.lastIssuedRequestId = currentRequestId;



        // Cancel any in-flight request
        if (this.currentAbortController) {
            this.currentAbortController.abort();
        }

        // Create new AbortController for this request
        this.currentAbortController = new AbortController();
        const signal = this.currentAbortController.signal;

        if (resetPagination) {
            this.pagination.currentPage = 1;
            this.pagination.offset = 0;
        }

        this.ui.isLoading = true;
        this.ui.error = null;
        // Update visibility immediately (shows skeleton)
        this.updateVisibility();
        this.executedWhere = this.where; // Snapshot the query

        // CRITICAL: Set search source BEFORE clearing results
        // This prevents race condition where map watcher fires with old source value
        // causing unwanted fitBounds() calls during bounds-based searches
        this._lastSearchSource = source;

        try {
            const { listings, count, listingPrices, mapPoints } = await this.fetchListings(signal);

            // CRITICAL: Only accept if this is still the latest request we issued
            // Ignores out-of-order arrivals (stale responses)
            if (requestId !== null && requestId !== this.lastIssuedRequestId) {
                if (window.__AVARI_DEBUG_MAP_SEARCH) {
                    console.log('[SEARCH] Ignoring stale response', requestId, 'vs latest', this.lastIssuedRequestId);
                }
                return;
            }

            if (window.__AVARI_DEBUG_MAP_SEARCH) {
                console.log('[SEARCH] Accepted request', requestId, 'source:', source, 'listings:', listings.length);
            }

            if (!listings.length) {
                this.results.listings = [];
                this.results.mapPoints = [];
                this.results.listingPrices = {};
                this.results.totalCount = 0;
                this.results.filteredCount = 0;
                this.pagination.totalPages = 0;
                this.ui.hasSearched = true;
                this.syncUrlState();
                viewItemList(this);
                return;
            }

            // Update mapPoints only on non-pagination requests (mapPoints are identical across pages)
            if (source !== 'pagination') {
                this.results.mapPoints = mapPoints;
            }

            // Store listings (Server-side pagination: "listings" is just the current page)
            this.results.listings = listings;
            // CRITICAL: use API total count for pagination, NOT array length
            this.results.totalCount = count || listings.length;
            this.results.filteredCount = count || listings.length;

            // Phase 2: Get availability/pricing if dates are selected
            if (this.hasDates()) {
                let constraintPrices;
                let availableIds;

                if (listingPrices && Object.keys(listingPrices).length > 0) {
                    // Prices were included inline from actionIndex — skip separate API call
                    constraintPrices = listingPrices;
                    availableIds = Object.keys(listingPrices).map(String);
                } else {
                    // Fallback: fetch constraints separately (browse or legacy)
                    const candidateIds = listings
                        .map(l => String(l.guestyListingId))
                        .filter(Boolean);

                    const constraints = await this.fetchConstraints(candidateIds);
                    constraintPrices = constraints.listingPrices || {};
                    availableIds = constraints.availableListingIds?.map(String) ?? null;
                }

                this.results.listingPrices = constraintPrices;
                this.results.pricedNights = this.nights;

                // Filter listings to only available ones
                let filteredListings = listings;
                if (availableIds) {
                     filteredListings = listings.filter(l => {
                        const id = String(l.guestyListingId);
                        return availableIds.includes(id);
                    });
                } else {
                     filteredListings = listings.filter(l => {
                        const id = String(l.guestyListingId);
                        return this.results.listingPrices[id] !== undefined;
                    });
                }

                // Optimize: Only update if the list actually changed to prevent double-triggering map watcher
                const prevIds = this.results.listings.map(l => l.guestyListingId || l.id).join(',');
                const newIds = filteredListings.map(l => l.guestyListingId || l.id).join(',');

                if (prevIds !== newIds) {
                    this.results.listings = filteredListings;
                }
            } else {
                this.results.listingPrices = {};
                this.results.pricedNights = 0;
            }

            // Update pagination
            this.pagination.totalPages = Math.ceil(
                this.results.filteredCount / this.pagination.limit
            );

            this.ui.hasSearched = true;
            this.syncUrlState();
            viewItemList(this);

        } catch (error) {
            // Don't show error for cancelled requests
            if (error.name === 'AbortError') {
                if (window.__AVARI_DEBUG_MAP_SEARCH) {
                    console.log('[SEARCH] Request cancelled (AbortError)');
                }
                return;
            }

            console.error('Search error:', error);
            this.ui.error = 'Unable to load search results. Please try again.';
        } finally {
            // Only turn off loading if this was the last request issued
            if (this.lastIssuedRequestId === currentRequestId) {
                this.ui.isLoading = false;
            }
            // Always update visibility at the end
            this.updateVisibility();
        }

    },

    // Phase 1: Fetch listings from search index
    async fetchListings(signal) {
        const params = new URLSearchParams();

        // Market/location (maps "where" to "market")
        if (this.where) {
            params.set('market', this.where);
        }

        // Coordinate-based proximity search
        if (this.whereData?.lat != null && this.whereData?.lng != null) {
            params.set('lat', this.whereData.lat);
            params.set('lng', this.whereData.lng);
        }

        // Sort order
        if (this.sort !== 'relevance') {
            params.set('sort', this.sort);
        }

        // Dates - CRITICAL for backend to switch to "Date Mode" (Total Price filtering)
        if (this.dates) {
            params.set('dates', this.dates);
        }

        // Bedrooms filter - pass as 'bedrooms' parameter (maps to numberOfBedrooms in API)
        if (this.filters.bedrooms > 1) {
            params.set('bedrooms', this.filters.bedrooms);
        }

        // Beds filter
        if (this.filters.beds > 1) {
            params.set('beds', this.filters.beds);
        }

        // Bathrooms filter
        if (this.filters.bathrooms > 1) {
            params.set('bathrooms', this.filters.bathrooms);
        }

        // Amenities filter
        if (this.filters.amenities.length) {
            params.set('amenities', this.filters.amenities.join(','));
        }

        // Property Type filter
        if (this.filters.propertyType.length) {
            params.set('propertyType', this.filters.propertyType.join(','));
        }

        // Accessibility Features filter
        if (this.filters.accessibilityFeatures.length) {
            params.set('accessibilityFeatures', this.filters.accessibilityFeatures.join(','));
        }

        // Price filters
        if (this.filters.priceMin !== this.priceDefaults.min) {
            params.set('minPrice', this.filters.priceMin);
        }
        if (this.filters.priceMax !== this.priceDefaults.max) {
            params.set('maxPrice', this.filters.priceMax);
        }

        // Map bounds filter (for search-as-you-move)
        if (this.bounds) {
            params.set('bounds', JSON.stringify(this.bounds));
        }

        // Server-side Pagination
        params.set('limit', this.pagination.limit);
        params.set('offset', this.pagination.offset);



        const url = `/guesty/search/index?${params.toString()}`;
        console.log('Search Store: Fetching listings', url);
        console.log('Search StoreParams:', {
            where: this.where,
            market: params.get('market'),
            params: params.toString()
        });

        const response = await fetch(url, {
            method: 'GET',
            credentials: 'include',
            signal: signal, // Enable request cancellation
            headers: {
                'Accept': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
            },
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (!data.success) {
            throw new Error(data.error || 'Failed to fetch listings');
        }

        // Capture backend message (e.g., "Please select a destination")
        if (data.message) {
            this.ui.backendMessage = data.message;
        } else {
            this.ui.backendMessage = null;
        }

        const results = data.results || [];

        // Debug: Log first listing structure to see what fields are available
        if (results.length > 0) {
            console.log('Sample listing structure (Phase 1):', {
                id: results[0].guestyListingId || results[0]._id || results[0].id,
                bedrooms: results[0].bedrooms,
                accommodates: results[0].accommodates,
                beds: results[0].beds,
                bathrooms: results[0].bathrooms,
                images: results[0].images?.length,
                pictures: results[0].pictures?.length,
                allKeys: Object.keys(results[0])
            });
        }

        return { listings: results, count: data.count || 0, listingPrices: data.listingPrices || null, mapPoints: data.mapPoints || [] };
    },

    // Phase 2: Fetch availability/pricing constraints
    async fetchConstraints(candidateListingIds) {
        const [checkIn, checkOut] = this.parseDates();

        console.log('fetchConstraints: dates=', this.dates, 'parsed=', checkIn, checkOut);

        if (!checkIn || !checkOut) {
            return { ok: true, count: candidateListingIds.length, listingPrices: {} };
        }

        const params = new URLSearchParams();
        params.set('checkIn', checkIn);
        params.set('checkOut', checkOut);
        params.set('guests', this.guests);

        // Price filters
        if (this.filters.priceMin !== this.priceDefaults.min) {
            params.set('minPrice', this.filters.priceMin);
        }
        if (this.filters.priceMax !== this.priceDefaults.max) {
            params.set('maxPrice', this.filters.priceMax);
        }

        // Bedrooms/beds/bathrooms filters (for API-level filtering in Phase 2)
        console.log('fetchConstraints: filters.bedrooms=', this.filters.bedrooms);
        if (this.filters.bedrooms > 1) {
            params.set('bedrooms', this.filters.bedrooms);
        }
        if (this.filters.beds > 1) {
            params.set('beds', this.filters.beds);
        }
        if (this.filters.bathrooms > 1) {
            params.set('bathrooms', this.filters.bathrooms);
        }

        // Candidate IDs
        candidateListingIds.forEach(id => {
            params.append('candidateListingIds[]', id);
        });

        const url = `/guesty/search/constraints?${params.toString()}`;
        console.log('fetchConstraints: URL=', url);

        const response = await fetch(url, {
            method: 'GET',
            credentials: 'include',
            headers: {
                'Accept': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
            },
        });

        console.log('fetchConstraints: status=', response.status);

        if (!response.ok) {
            const text = await response.text();
            console.error('fetchConstraints: error response=', text);
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log('fetchConstraints: response data=', data);

        if (!data.ok) {
            throw new Error('Failed to fetch availability');
        }

        return data;
    },

    // Fetch popup HTML for a single listing (lazy-load for non-page markers)
    async fetchPopup(entryId) {
        const response = await fetch(`/guesty/search/popup?id=${entryId}`, {
            credentials: 'include',
            headers: {
                'Accept': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
            },
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        if (!data.success) {
            throw new Error(data.error || 'Failed to fetch popup');
        }

        return data.popupHtml;
    },

    // Fetch popup HTML for multiple listings in a single request
    async fetchPopupBatch(entryIds) {
        const params = new URLSearchParams();
        entryIds.forEach(id => params.append('ids[]', id));

        const response = await fetch(`/guesty/search/popup-batch?${params.toString()}`, {
            credentials: 'include',
            headers: {
                'Accept': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
            },
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        if (!data.success) {
            throw new Error(data.error || 'Failed to fetch popup batch');
        }

        return data.popups || {};
    },

    // Pagination methods
    async goToPage(page) {
        if (page < 1 || page > this.pagination.totalPages) return;
        if (page === this.pagination.currentPage) return;

        this.pagination.currentPage = page;
        this.pagination.offset = (page - 1) * this.pagination.limit;

        // Server-side pagination: Fetch new page
        this.ui.isLoadingMore = true;
        await this.performSearch({ resetPagination: false, source: 'pagination' });
        this.ui.isLoadingMore = false;

        // Scroll to top of results
        window.scrollTo({ top: 0, behavior: 'smooth' });
    },

    nextPage() {
        this.goToPage(this.pagination.currentPage + 1);
    },

    prevPage() {
        this.goToPage(this.pagination.currentPage - 1);
    },

    // Apply filters (called from modal)
    applyFilters() {
        // Cancel any in-flight request to prevent stale results
        if (this.currentAbortController) {
            this.currentAbortController.abort();
            this.currentAbortController = null;
        }

        // Cancel pending timeout
        if (window.Alpine?.store?.('searchMap')?.cancelPendingBoundsSearch) {
            window.Alpine.store('searchMap').cancelPendingBoundsSearch();
        }

        this.performSearch({ resetPagination: true, source: 'filters' });
        Alpine.store('modals').close('filters-modal');
    },

    // Set map bounds (called when user drags/zooms map)
    setBounds(bounds) {
        this.bounds = bounds;
        this.boundsSearchEnabled = true;
    },

    // Clear bounds (called when user changes filters/destination)
    clearBounds() {
        this.bounds = null;
        this.boundsSearchEnabled = false;
        this.mapAreaLabel = null;
        this._visualWhereMatch = null;
    },

    // Search within current map bounds
    async searchInBounds(requestId) {
        // Clear destination-based search state
        this.where = '';
        this.whereData = null;
        this.mapAreaLabel = 'Homes in map area';
        this._visualWhereMatch = null;

        this.pagination.currentPage = 1;
        this.pagination.offset = 0;
        await this.performSearch({ resetPagination: false, requestId, source: 'bounds' });
    },

    // Reset filters to defaults
    resetFilters() {
        this.filters.propertyType = [];
        this.filters.priceMin = this.priceDefaults.min;
        this.filters.priceMax = this.priceDefaults.max;
        this.filters.bedrooms = 1;
        this.filters.beds = 1;
        this.filters.bathrooms = 1;
        this.filters.amenities = [];
        this.filters.accessibilityFeatures = [];
    },

    // Live filter count: fetch count-only from backend (skips hydration)
    async fetchFilterCount() {
        if (this._filterCountController) {
            this._filterCountController.abort();
        }
        this._filterCountController = new AbortController();
        this.filterCountLoading = true;

        try {
            const params = new URLSearchParams();
            if (this.where) params.set('market', this.where);
            if (this.whereData?.lat != null && this.whereData?.lng != null) {
                params.set('lat', this.whereData.lat);
                params.set('lng', this.whereData.lng);
            }
            if (this.dates) params.set('dates', this.dates);
            if (this.filters.bedrooms > 1) params.set('bedrooms', this.filters.bedrooms);
            if (this.filters.beds > 1) params.set('beds', this.filters.beds);
            if (this.filters.bathrooms > 1) params.set('bathrooms', this.filters.bathrooms);
            if (this.filters.amenities.length) params.set('amenities', this.filters.amenities.join(','));
            if (this.filters.propertyType.length) params.set('propertyType', this.filters.propertyType.join(','));
            if (this.filters.accessibilityFeatures.length) params.set('accessibilityFeatures', this.filters.accessibilityFeatures.join(','));
            if (this.filters.priceMin !== this.priceDefaults.min) params.set('minPrice', this.filters.priceMin);
            if (this.filters.priceMax !== this.priceDefaults.max) params.set('maxPrice', this.filters.priceMax);
            if (this.bounds) params.set('bounds', JSON.stringify(this.bounds));
            params.set('countOnly', 'true');

            const resp = await fetch(`/guesty/search/index?${params}`, {
                signal: this._filterCountController.signal,
                credentials: 'include',
                headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            });
            const data = await resp.json();
            if (data.success) {
                this.liveFilterCount = data.count;
            }
        } catch (e) {
            if (e.name !== 'AbortError') console.error('Filter count error:', e);
        } finally {
            this.filterCountLoading = false;
        }
    },

    // Initialize debounced filter count watcher (called once from modal x-init)
    initFilterCountWatcher() {
        if (!this._debouncedFilterCount) {
            let timer;
            this._debouncedFilterCount = () => {
                clearTimeout(timer);
                timer = setTimeout(() => this.fetchFilterCount(), 300);
            };
        }
    },

    // Get count of active (non-default) filters
    getActiveFilterCount() {
        let count = 0;
        if (this.filters.propertyType.length) count++;
        if (this.filters.priceMin !== this.priceDefaults.min) count++;
        if (this.filters.priceMax !== this.priceDefaults.max) count++;
        if (this.filters.bedrooms > 1) count++;
        if (this.filters.beds > 1) count++;
        if (this.filters.bathrooms > 1) count++;
        if (this.filters.amenities.length) count++;
        if (this.filters.accessibilityFeatures.length) count++;
        return count;
    },

    updateGuests() {
        const adults = Number.isFinite(Number(this.adults)) ? Number(this.adults) : 0;
        const children = Number.isFinite(Number(this.children)) ? Number(this.children) : 0;
        this.adults = Math.max(0, adults);
        this.children = Math.max(0, children);
        this.guests = this.adults + this.children;
    },

    reset() {
        this.where = '';
        this.whereData = null;
        this.mapAreaLabel = null;
        this._visualWhereMatch = null;
        this.dates = '';
        this.adults = 1;
        this.children = 0;
        this.pets = false;
        this.updateGuests();
        this.resetFilters();
        this.clearBounds();
        this.results = { listings: [], mapPoints: [], listingPrices: {}, pricedNights: 0, totalCount: 0, filteredCount: 0 };
        this.pagination = { currentPage: 1, limit: 12, offset: 0, totalPages: 1 };
        this.ui.hasSearched = false;
        this.ui.error = null;
        this.syncUrlState();
    },

    matches(option, query) {
        const parsedQuery = normalize(query);
        if (!parsedQuery) return true;
        return normalize(option).includes(parsedQuery);
    },

    // Helper: Formatted guests label (e.g. "1 guest", "3 guests")
    get guestsLabel() {
        if (this.guests <= 0) return '';
        return this.guests === 1 ? '1 Guest' : `${this.guests} Guests`;
    },

    // Helper: Check if dates are selected
    hasDates() {
        return this.dates && this.dates.includes(' to ');
    },

    // Helper: Parse dates from "YYYY-MM-DD to YYYY-MM-DD" format
    parseDates() {
        if (!this.hasDates()) return [null, null];
        const parts = this.dates.split(' to ');
        return [parts[0]?.trim(), parts[1]?.trim()];
    },

    parseDateOnly(value) {
        if (typeof value !== 'string') return null;
        const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (!match) return null;

        const year = Number(match[1]);
        const month = Number(match[2]) - 1;
        const day = Number(match[3]);
        return new Date(year, month, day);
    },

    // Helper: Get number of nights from selected dates
    get nights() {
        const [checkIn, checkOut] = this.parseDates();
        if (!checkIn || !checkOut) return 0;
        const msPerDay = 86400000;
        const start = this.parseDateOnly(checkIn);
        const end = this.parseDateOnly(checkOut);
        if (!start || !end) return 0;
        return Math.round((end - start) / msPerDay);
    },

    // Helper: Price label based on nights the prices were fetched for
    get priceLabel() {
        const n = this.results.pricedNights;
        if (n > 1) return '/ ' + n + ' nights';
        if (n === 1) return 'total';
        return '/ night';
    },

    // Helper: Get listing price from constraints
    getListingPrice(listingId) {
        return this.results.listingPrices[String(listingId)] ?? null;
    },

    // Helper: Get current page's listings (sliced for pagination)
    getPaginatedListings() {
        // Server-side pagination: The "listings" array IS the paginated slice
        return this.results.listings;
    },


    // Helper: Format price as USD currency
    formatPrice(price) {
        if (!price) return null;
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
        }).format(price);
    },

    // Helper: Format dates for display
    // "2026-02-25" → "Feb 25", "2026-01-05 to 2026-01-07" → "Jan 5-7"
    get formattedDates() {
        if (!this.dates) return this.dates;

        const parts = this.dates.split(' to ');
        const startDate = new Date(parts[0].trim() + 'T00:00:00');
        const endDate = parts[1] ? new Date(parts[1].trim() + 'T00:00:00') : startDate;

        if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return this.dates;

        const currentYear = new Date().getFullYear();
        const startMonth = startDate.toLocaleString('en-US', { month: 'short' });
        const startDay = startDate.getDate();
        const startYear = startDate.getFullYear();

        // Single date or same date on both sides
        if (startDate.getTime() === endDate.getTime()) {
            return startYear === currentYear
                ? `${startMonth} ${startDay}`
                : `${startMonth} ${startDay}, ${startYear}`;
        }

        const endMonth = endDate.toLocaleString('en-US', { month: 'short' });
        const endDay = endDate.getDate();
        const endYear = endDate.getFullYear();
        const sameMonth = startDate.getMonth() === endDate.getMonth() && startYear === endYear;
        const sameYear = startYear === endYear;
        const showYear = startYear !== currentYear || endYear !== currentYear;

        if (sameMonth) {
            return showYear
                ? `${startMonth} ${startDay}-${endDay}, ${startYear}`
                : `${startMonth} ${startDay}-${endDay}`;
        }
        if (sameYear) {
            return showYear
                ? `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${startYear}`
                : `${startMonth} ${startDay} - ${endMonth} ${endDay}`;
        }
        return `${startMonth} ${startDay}, ${startYear} - ${endMonth} ${endDay}, ${endYear}`;
    },

    get splitDates() {
        if (!this.dates) return null;

        const fmt = (date) => `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
        const parts = this.dates.split(' to ');
        const startDate = new Date(parts[0].trim() + 'T00:00:00');
        const endDate = parts[1] ? new Date(parts[1].trim() + 'T00:00:00') : null;

        if (isNaN(startDate.getTime())) return null;

        const checkIn = fmt(startDate);
        const checkOut = endDate && !isNaN(endDate.getTime()) ? fmt(endDate) : null;

        return { checkIn, checkOut };
    },

    // Toggle map mode
    toggleMapMode() {
        window.scrollTo({ top: 0, behavior: 'instant' });
        this.ui.mapMode = !this.ui.mapMode;
    },
});
